import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Plus, Edit2, Copy, Trash2, Eye, RefreshCw,
  ToggleLeft, ToggleRight, CodeXml, X, ArrowRight,
  CheckCircle2, CircleDashed,
} from "lucide-react";
import { toast } from "react-toastify";
import { getAuthHeaders } from "../services/authHeaders";
import ConfirmDialog from "../components/ConfirmDialog";

const XANO_BASE_URL = "https://api.sobevenda.com.br/api-proxy/script";

function fmtDate(dt) {
  if (!dt) return "-";
  try { return new Date(dt).toLocaleString("pt-BR"); } catch { return String(dt); }
}

function getScriptVariants(script) {
  const baseUrl = `https://api.sobevenda.com.br/sobescript/${script.user_id}-${script.uuid}`;
  return {
    singleScript: `<script defer src="${baseUrl}"></script>`,
    bridgeScript: `<script defer src="${baseUrl}?funnel=bridge_vsl&stage=bridge_page"></script>`,
    vslScript:    `<script defer src="${baseUrl}?funnel=bridge_vsl&stage=vsl_page"></script>`,
  };
}

async function copyText(text, successMessage = "Script copiado!") {
  if (!text) { toast.error("Nenhum script encontrado."); return; }
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    toast.success(successMessage);
  } catch { toast.error("Não foi possível copiar o script."); }
}

// ─── Modal de scripts de funil ────────────────────────────────────────────────

function FunnelScriptsModal({ script, onClose }) {
  if (!script) return null;
  const { bridgeScript, vslScript } = getScriptVariants(script);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" aria-modal="true" role="dialog">
      <div className="absolute inset-0 bg-black/50 dark:bg-black/70" onClick={onClose} />

      <div className="relative z-10 w-full max-w-3xl rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-lg">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Scripts do funil</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Projeto: <span className="font-medium">{script.project_name || "—"}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scripts */}
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { label: "Script da Bridge Page", hint: "Use na primeira página do funil.", code: bridgeScript, msg: "Script da Bridge copiado!" },
            { label: "Script da VSL",         hint: "Use na página da VSL.",           code: vslScript,    msg: "Script da VSL copiado!" },
          ].map(({ label, hint, code, msg }) => (
            <div key={label} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-800/40">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">{label}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{hint}</p>
                </div>
                <button
                  onClick={() => copyText(code, msg)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs shrink-0"
                >
                  <Copy className="w-3.5 h-3.5" /> Copiar
                </button>
              </div>
              <textarea
                readOnly
                value={code}
                className="w-full min-h-[120px] px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs text-slate-800 dark:text-slate-100 font-mono resize-none"
              />
            </div>
          ))}
        </div>

        {/* Instrução */}
        <div className="mt-4 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
          <p className="text-xs text-slate-600 dark:text-slate-300">
            <strong className="text-slate-700 dark:text-slate-200">Como usar:</strong>{" "}
            Cole o script da Bridge na bridge page e o script da VSL na página da VSL.
          </p>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function SobeScriptsList() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteUuid, setConfirmDeleteUuid] = useState(null);
  const [copyModalScript, setCopyModalScript] = useState(null);

  async function fetchScripts() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${XANO_BASE_URL}/list-sobescripts`, { headers: getAuthHeaders() });
      setScripts(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Erro ao buscar SobeScripts.");
      setScripts([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchScripts(); }, []);

  async function handleDelete() {
    const uuid = confirmDeleteUuid;
    setConfirmDeleteUuid(null);
    try {
      await axios.delete(`${XANO_BASE_URL}/delete-sobescript`, { headers: getAuthHeaders(), data: { uuid } });
      toast.success("SobeScript excluído com sucesso!");
      fetchScripts();
    } catch {
      toast.error("Erro ao excluir o SobeScript.");
    }
  }

  async function handleToggleActive(script) {
    try {
      await axios.patch(`${XANO_BASE_URL}/update-sobescript`, { uuid: script.uuid, active: !script.active }, { headers: getAuthHeaders() });
      toast.success(script.active ? "SobeScript desativado!" : "SobeScript ativado!");
      fetchScripts();
    } catch {
      toast.error("Erro ao atualizar status do SobeScript.");
    }
  }

  async function handleCopyScript(script) {
    if (!script.user_id || !script.uuid) { toast.error("Dados do script não encontrados!"); return; }

    const funnelMode = String(script.funnel_mode || "").toLowerCase().trim();
    const isFunnel = script.is_funnel === 1 || script.is_funnel === "1" || script.is_funnel === true;
    const isBridgeVsl = funnelMode === "bridge_vsl" || isFunnel;

    if (isBridgeVsl) { setCopyModalScript(script); return; }

    const { singleScript } = getScriptVariants(script);
    await copyText(singleScript, "Script copiado com sucesso!");
  }

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <CodeXml className="w-6 h-6" />
              SobeScripts
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Gerencie seus scripts de rastreamento e conversão.
            </p>
          </div>

          <button
            onClick={fetchScripts}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 shrink-0 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </button>
        </div>

        {/* AÇÃO PRIMÁRIA */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {loading ? "Carregando scripts..." : `${scripts.length} ${scripts.length === 1 ? "script" : "scripts"}`}
          </p>
          <Link
            to="/sobescript"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            <Plus className="w-4 h-4" />
            Novo SobeScript
          </Link>
        </div>

        {/* LISTAGEM — CARDS NO MOBILE / TABELA NO DESKTOP */}

        {/* Mobile/Tablet: Cards (visível < lg) */}
        <div className="lg:hidden space-y-3">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-slate-500 text-sm">
              Carregando scripts...
            </div>
          ) : scripts.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Nenhum SobeScript encontrado.</p>
              <Link
                to="/sobescript"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
              >
                <Plus className="w-4 h-4" /> Novo SobeScript
              </Link>
            </div>
          ) : (
            scripts.map((script) => (
              <div key={script.uuid} className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
                {/* Nome + status */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Projeto</div>
                    <div className={`text-sm font-bold break-words ${script.active ? "text-slate-900 dark:text-slate-100" : "text-slate-500 dark:text-slate-400"}`}>
                      {script.project_name}
                    </div>
                  </div>
                  {script.active ? (
                    <span className="shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Ativo
                    </span>
                  ) : (
                    <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      Inativo
                    </span>
                  )}
                </div>

                {/* Detalhes */}
                <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Plataforma</div>
                    <div className="text-slate-700 dark:text-slate-300">{script.platform || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Conversão</div>
                    <div className="text-slate-700 dark:text-slate-300">{script.conversion_name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Visitas</div>
                    <div className="text-slate-700 dark:text-slate-300">{script.visit_count ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Modo</div>
                    <div className="text-slate-700 dark:text-slate-300">
                      {script.auto_conversion === "yes" ? "Automático" : "Manual"}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">Criado em</div>
                    <div className="text-slate-600 dark:text-slate-300 text-xs">{fmtDate(script.created_at)}</div>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                  <button
                    onClick={() => handleToggleActive(script)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                  >
                    {script.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                    {script.active ? "Desativar" : "Ativar"}
                  </button>

                  <button
                    onClick={() => handleCopyScript(script)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                  >
                    <Copy className="w-4 h-4" /> Copiar
                  </button>

                  <Link
                    to={`/sobescripts-analytics/${script.uuid}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                  >
                    <Eye className="w-4 h-4" /> Dados
                  </Link>

                  <Link
                    to={`/sobescript/edit/${script.uuid}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                  >
                    <Edit2 className="w-4 h-4" /> Editar
                  </Link>

                  <button
                    onClick={() => setConfirmDeleteUuid(script.uuid)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm"
                  >
                    <Trash2 className="w-4 h-4" /> Excluir
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop: Tabela (visível >= lg) */}
        <div className="hidden lg:block rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full table-fixed text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/40">
                <tr className="text-left">
                  <th className="px-4 py-3 w-[20%] font-semibold text-slate-700 dark:text-slate-200">Projeto</th>
                  <th className="px-4 py-3 w-[10%] font-semibold text-slate-700 dark:text-slate-200">Plataforma</th>
                  <th className="px-4 py-3 w-[12%] font-semibold text-slate-700 dark:text-slate-200">Conversão</th>
                  <th className="px-4 py-3 w-[13%] font-semibold text-slate-700 dark:text-slate-200">Criado em</th>
                  <th className="px-4 py-3 w-[7%] font-semibold text-slate-700 dark:text-slate-200">Visitas</th>
                  <th className="px-4 py-3 w-[10%] font-semibold text-slate-700 dark:text-slate-200">Modo</th>
                  <th className="px-4 py-3 w-[8%] font-semibold text-slate-700 dark:text-slate-200">Status</th>
                  <th className="px-4 py-3 w-[20%] text-right font-semibold text-slate-700 dark:text-slate-200">Ações</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <>
                    {[1, 2, 3].map((i) => (
                      <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                        {Array.from({ length: 8 }).map((_, j) => (
                          <td key={j} className="px-4 py-3">
                            <div className="h-4 w-full max-w-[10rem] animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                ) : scripts.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-sm" colSpan={8}>
                      <div className="text-center">
                        <p className="text-slate-500 mb-4">Nenhum SobeScript encontrado.</p>
                        <Link
                          to="/sobescript"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                        >
                          <Plus className="w-4 h-4" /> Novo SobeScript
                        </Link>
                      </div>
                    </td>
                  </tr>
                ) : (
                  scripts.map((script) => (
                    <tr key={script.uuid} className="border-t border-slate-100 dark:border-slate-800 align-middle hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="px-4 py-3">
                        <span className={`font-medium break-words ${script.active ? "text-slate-900 dark:text-slate-100" : "text-slate-500 dark:text-slate-400"}`}>
                          {script.project_name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{script.platform || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{script.conversion_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{fmtDate(script.created_at)}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{script.visit_count ?? 0}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          script.auto_conversion === "yes"
                            ? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                            : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                        }`}>
                          {script.auto_conversion === "yes" ? "Automático" : "Manual"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {script.active ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Ativo
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                            Inativo
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleToggleActive(script)}
                            title={script.active ? "Desativar" : "Ativar"}
                            className="inline-flex items-center justify-center p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            {script.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>

                          <button
                            onClick={() => handleCopyScript(script)}
                            title="Copiar script"
                            className="inline-flex items-center justify-center p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            <Copy className="w-4 h-4" />
                          </button>

                          <Link
                            to={`/sobescripts-analytics/${script.uuid}`}
                            title="Ver dados"
                            className="inline-flex items-center justify-center p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>

                          <Link
                            to={`/sobescript/edit/${script.uuid}`}
                            title="Editar"
                            className="inline-flex items-center justify-center p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Link>

                          <button
                            onClick={() => setConfirmDeleteUuid(script.uuid)}
                            title="Excluir"
                            className="inline-flex items-center justify-center p-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* Modal de funil */}
      <FunnelScriptsModal script={copyModalScript} onClose={() => setCopyModalScript(null)} />

      {/* Confirmação de exclusão */}
      <ConfirmDialog
        open={confirmDeleteUuid !== null}
        title="Excluir SobeScript"
        message="Tem certeza que deseja excluir este SobeScript? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteUuid(null)}
      />
    </div>
  );
}